import { Loader } from "@googlemaps/js-api-loader";
import { } from "google-maps";
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import { Roles } from "../../models/emums/roles";
import ContractorSearchItemModel from "../../models/rest/contractor/contractorSearchItemModel";
import LocalStorageApplicationStateDataModel from "../../models/localStorageApplicationStateDataModel";
import LogisticLoadRestModel from "../../models/rest/logistic/logisticLoadRestModel";
import SupervisorUnloadingPlaceForMapListRestModel from "../../models/rest/supervisor/supervisorUnloadingPlaceForMapListRestModel";
import VehicleRestModel from "../../models/rest/vehicle/vehicleRestModel";
import RegisteredVehicleDialog from "./dialog/registered-vehicle-dialog.vue";
import InfoWindow from "./info-window.vue";
import LogisticLoadInfoWindow from "./logistic-load-info-window.vue";
import SupervisorUnloadingPlaceForMapInfoWindow from "./supervisor-unloading-place-for-map-info-window.vue";
import i18n from "@/assets/i18n";

@Component({
    components: {
        "info-window": InfoWindow,
        "logistic-load-info-window": LogisticLoadInfoWindow,
        "registered-vehicle-dialog": RegisteredVehicleDialog,
        "supervisor-unloading-place-for-map-info-window": SupervisorUnloadingPlaceForMapInfoWindow,
    }
})

export default class Map extends Vue {
    map: google.maps.Map | undefined;
    yesNoFilterOptions = [
        { text: i18n.t("map.all"), value: "all" },
        { text: i18n.t("map.yes"), value: "yes" },
        { text: i18n.t("map.no"), value: "no" },
    ];
    showInfoWindow = false;
    showLogiscticLoadInfoWindow = false;
    showSupervisorUnloadingPlaceForMapInfoWindow = false;
    currentVehicleMarkers: any = {};
    currentRegisteredVehicleMarkers: any = {};
    currentLogisticLoadMarkers: any = {};
    currentSupervisorUnloadingPlaceForMapMarkers: any = {};
    infoWindowElement = document.getElementById("info-window") as HTMLElement;
    logisticLoadInfoWindowElement = document.getElementById("logistic-load-info-window") as HTMLElement;
    supervisorUnloadingPlaceForMapInfoWindowElement = document.getElementById("supervisor-unloading-place-for-map-info-window") as HTMLElement;
    vMarkers: any[] = [];
    previewVehicleKey: number | null = null;
    currentInfoWindow: any = {};
    directionsService: google.maps.DirectionsService | undefined;
    directionsRenderer: google.maps.DirectionsRenderer | undefined;
    waypts: google.maps.DirectionsWaypoint[] = [];
    computedDistance: string = "";
    computedTime: string = "";
    interval: number | undefined = 0;
    showRegisteredVehicleDialog = false;
    showRegisteredVehicleDialogInfo = false;
    registeredVehicleDialogInfoMessage = "";
    contractorMapSearch = "";

    loader = new Loader({
        apiKey: this.googleMapsApiKey,
        libraries: ["places"]
    });

    get vehicleModifiedTime(): string {
        return this.$store.direct.state.vehicle.vehicleModifiedTime;
    }

    get googleMapsApiKey(): string {
        return this.$store.direct.state.auth.googleMapsApiKey;
    }

    get showInfoDialog(): boolean {
        return this.$store.direct.state.vehicle.showVehicleMapInfoDialog;
    }
    get infoDialogMessage(): string {
        return this.$store.direct.state.vehicle.vehicleMapInfoDialogMessage;
    }

    get infoDialogColor(): string {
        return this.$store.direct.state.vehicle.vehicleMapInfoDialogColor;
    }

    get vehicleMapList(): VehicleRestModel[] {
        return this.$store.direct.state.vehicle.vehicleMapList;
    }

    set vehicleMapList(val: VehicleRestModel[]) {
        this.$store.direct.commit.vehicle.setVehicleMapList(val);
    }

    get registeredVehicleMapList(): VehicleRestModel[] {
        return this.$store.direct.state.vehicle.registeredVehicleMapList;
    }

    set registeredVehicleMapList(val: VehicleRestModel[]) {
        this.$store.direct.commit.vehicle.setRegisteredVehicleMapList(val);
    }

    get logisticLoadMapList(): LogisticLoadRestModel[] {
        return this.$store.direct.state.logistic.logisticLoadMapList;
    }

    set logisticLoadMapList(val: LogisticLoadRestModel[]) {
        this.$store.direct.commit.logistic.setLogisticLoadMapList(val);
    }

    get supervisorUnloadingPlaceForMapList(): SupervisorUnloadingPlaceForMapListRestModel[] {
        return this.$store.direct.state.supervisor.supervisorUnloadingPlaceForMapList;
    }

    set supervisorUnloadingPlaceForMapList(val: SupervisorUnloadingPlaceForMapListRestModel[]) {
        this.$store.direct.commit.supervisor.setSupervisorUnloadingPlaceForMapList(val);
    }

    get vehicle(): VehicleRestModel {
        return this.$store.direct.state.vehicle.vehicle;
    }

    get logisticLoad(): LogisticLoadRestModel {
        return this.$store.direct.state.logistic.logisticLoad;
    }

    get localStorageApplicationStateData(): LocalStorageApplicationStateDataModel {
        return this.$store.direct.state.common.localStorageApplicationStateData;
    }

    get currentUserRoles(): string[] {
        return this.$store.direct.state.auth.userRoles;
    }

    get userIsAdminDisponentAdminOrDisponent(): boolean {
        if (this.currentUserRoles.includes(Roles.Admin) || this.currentUserRoles.includes(Roles.Disponent) || this.currentUserRoles.includes(Roles.DisponentAdmin)) {
            return true;
        }
        return false;
    }

    get contractorFound(): ContractorSearchItemModel[] {
        return this.$store.direct.state.common.contractorSearch;
    }

    get contractorFoundWithSelectedItem(): ContractorSearchItemModel[] {
        const selectedContractor = this.localStorageApplicationStateData.contractorSearchItem as ContractorSearchItemModel | null;

        if (selectedContractor == null) {
            return this.contractorFound;
        }

        if (this.contractorFound.some((item: ContractorSearchItemModel) => item.value === selectedContractor.value)) {
            return this.contractorFound;
        }

        return [selectedContractor, ...this.contractorFound];
    }

    @Watch("contractorMapSearch")
    contractorMapSearchLoadItems(val: string) {
        this.$store.direct.dispatch.common.searchContractors({ searchText: val, contractorTypes: [], contractorPropertiesEnabled: [], source: "map", searchOnlyActive: true });
    }

    @Watch("localStorageApplicationStateData.contractorSearchItem", { immediate: true })
    contractorSearchItemChanged(val: ContractorSearchItemModel | null) {
        if (this.contractorMapSearch !== (this.localStorageApplicationStateData.contractorSearchItem?.text ?? "")) {
            this.contractorMapSearch = this.localStorageApplicationStateData.contractorSearchItem?.text ?? "";
        }
    }

    @Watch("vehicleModifiedTime")
    async vehicleModifiedTimeChanged() {
        if (this.localStorageApplicationStateData.mapPageVehicleLayer) {
            await this.$store.direct.dispatch.vehicle.loadVehicleMapList({
                adr: this.localStorageApplicationStateData.filterByAdr,
                tailLift: this.localStorageApplicationStateData.filterByTailLift,
                contractorSearchText: this.localStorageApplicationStateData.contractorSearchItem?.text
            });
        }
        if (this.localStorageApplicationStateData.mapPageRegisteredVehicleLayer) {
            await this.$store.direct.dispatch.vehicle.loadRegisteredVehicleMapList({
                contractorSearchText: this.localStorageApplicationStateData.contractorSearchItem?.text
            });
        }
        if (this.localStorageApplicationStateData.mapPageLogisticLoadLayer) {
            await this.$store.direct.dispatch.logistic.loadLogisticLoadMapList();
        }
        if (this.localStorageApplicationStateData.mapPageSupervisorUnloadingPlaceLayer) {
            await this.$store.direct.dispatch.supervisor.getSupervisorUnloadingPlaceForMapList(
                {
                    showBmwUnloadings: this.localStorageApplicationStateData.mapPageSupervisorBmwUnloadingPlaceLayer,
                    showEncoryUnloadings: this.localStorageApplicationStateData.mapPageSupervisorEncoryUnloadingPlaceLayer,
                    adr: this.localStorageApplicationStateData.filterByAdr,
                    tailLift: this.localStorageApplicationStateData.filterByTailLift,
                    contractorSearchText: this.localStorageApplicationStateData.contractorSearchItem?.text
                });
        }
        this.initialMarkers();
    }

    async mounted() {
        await this.refreshAllActiveFilters();
        await this.loadMap()
        this.interval = setInterval(async () => await this.$store.direct.dispatch.vehicle.checkChanges(), 30000)
    }

    destroyed() {
        clearInterval(this.interval);
        this.interval = undefined;
    }

    @Watch("localStorageApplicationStateData", { deep: true })
    async localStorageApplicationStateDataHandler() {
        this.$store.direct.dispatch.common.updateLocalStorageApplicationStateData();
    }

    async holdOnOffChildClick(vehicleId: number) {
        const vehicleIcon = await this.getVehicleIcon(this.vehicle, 'vehicle')
        const vehicleMarker = await this.currentVehicleMarkers[vehicleId]
        await vehicleMarker.marker.setIcon(vehicleIcon)
    }

    async unavailableVehicleChildClick(vehicleId: number) {
        await this.currentVehicleMarkers[vehicleId].infoWindow.close();
        await this.currentVehicleMarkers[vehicleId].marker.setMap(null);
        this.currentVehicleMarkers[vehicleId].marker = null;
        delete this.currentVehicleMarkers[vehicleId]
    }

    async unavailableSupervisorUnloadingPlaceForMapChildClick(supervisorUnloadingPlaceForMapId: number) {
        await this.currentSupervisorUnloadingPlaceForMapMarkers[supervisorUnloadingPlaceForMapId].infoWindow.close();
        await this.currentSupervisorUnloadingPlaceForMapMarkers[supervisorUnloadingPlaceForMapId].marker.setMap(null);
        this.currentSupervisorUnloadingPlaceForMapMarkers[supervisorUnloadingPlaceForMapId].marker = null;
        delete this.currentSupervisorUnloadingPlaceForMapMarkers[supervisorUnloadingPlaceForMapId];
    }

    async getDirectionClick(waystations: any[], avoidHighways: boolean, avoidTolls: boolean) {
        this.waypts = [];
        waystations.forEach((station: any) => {
            let latLng: google.maps.LatLng;

            if (station.fullPlaceName.lat == undefined) {
                latLng = new google.maps.LatLng(station.lat, station.lng)
            }
            else {
                latLng = new google.maps.LatLng(station.fullPlaceName.lat, station.fullPlaceName.lng)
            }
            this.waypts.push({ location: latLng })
        })

        const request: google.maps.DirectionsRequest = {
            origin: this.waypts[0] as google.maps.LatLng,
            destination: this.waypts[this.waypts.length - 1] as google.maps.LatLng,
            waypoints: this.waypts,
            travelMode: google.maps.TravelMode.DRIVING,
            avoidHighways: avoidHighways,
            avoidTolls: avoidTolls,
        };

        this.directionsService?.route(request, (result: google.maps.DirectionsResult, status: google.maps.DirectionsStatus) => {
            if (status == google.maps.DirectionsStatus.OK) {
                this.directionsRenderer?.setDirections(result);
                this.directionsRenderer?.setOptions({
                    markerOptions: {
                        visible: false
                    } as google.maps.MarkerOptions
                });
                this.computeTotalDistance(result);
                this.computeTotalTime(result);
            }
        })
    }

    computeTotalDistance(result: google.maps.DirectionsResult) {
        let total = 0;
        const myroute = result.routes[0];
        for (let i = 0; i < myroute.legs.length; i++) {
            total += myroute.legs[i].distance.value;
        }
        total = Math.round(total / 1000);
        this.computedDistance = total + " km";
    }

    computeTotalTime(result: google.maps.DirectionsResult) {
        let hours = 0;
        let minutes = 0;
        let totalTime = 0;
        const myroute = result.routes[0];
        for (let i = 0; i < myroute.legs.length; i++) {
            totalTime += myroute.legs[i].duration.value;
        }
        minutes = Math.round(totalTime / 60);
        hours = Math.floor(minutes / 60);
        minutes = (minutes % 60);
        this.computedTime = hours + ' h, ' + minutes + ' min'
    }

    vehicleExist(vehicle: any, vehicleCollection: any) {
        const vehicleId = vehicle.id.toString()
        const hasVehicle = Object.keys(vehicleCollection).includes(vehicleId)
        if (hasVehicle) {
            return vehicleCollection[parseInt(vehicleId)];
        }
        else {
            return null;
        }
    }

    registeredVehicleExist(registeredVehicle: any, registeredVehicleCollection: any) {
        const registeredVehicleId = registeredVehicle.id.toString()
        const hasRegisteredVehicle = Object.keys(registeredVehicleCollection).includes(registeredVehicleId)
        if (hasRegisteredVehicle) {
            return registeredVehicleCollection[parseInt(registeredVehicleId)];
        }
        else {
            return null;
        }
    }

    logisticLoadExist(logisticLoad: any, logisticLoadCollection: any) {
        const logisticLoadId = logisticLoad.id.toString()
        const hasLogisticLoad = Object.keys(logisticLoadCollection).includes(logisticLoadId)
        if (hasLogisticLoad) {
            return logisticLoadCollection[parseInt(logisticLoadId)];
        }
        else {
            return null;
        }
    }

    supervisorUnloadingPlaceForMapExist(supervisorUnloadingPlaceForMap: any, supervisorUnloadingPlaceForMapCollection: any) {
        const supervisorUnloadingPlaceForMapId = supervisorUnloadingPlaceForMap.id.toString()
        const hasSupervisorUnloadingPlaceForMap = Object.keys(supervisorUnloadingPlaceForMapCollection).includes(supervisorUnloadingPlaceForMapId)
        if (hasSupervisorUnloadingPlaceForMap) {
            return supervisorUnloadingPlaceForMapCollection[parseInt(supervisorUnloadingPlaceForMapId)];
        }
        else {
            return null;
        }
    }

    async setupMarkers(vehicleMarkers: any, registeredVehicleMarkers: any, logisticLoadMarkers: any, supervisorUnloadingPlaceForMapMarkers: any) {
        let i = 0
        Object.keys(this.currentVehicleMarkers).forEach(async key => {
            const currentVehicle = this.currentVehicleMarkers[parseInt(key)];
            const vehicleExist = this.vehicleExist(currentVehicle, vehicleMarkers)

            if (vehicleExist == null) {
                currentVehicle.marker.setMap(null);
                currentVehicle.marker = null;
                delete this.currentVehicleMarkers[parseInt(key)]
            }
        })

        Object.keys(vehicleMarkers).forEach(async key => {
            const vehicle = vehicleMarkers[parseInt(key)];
            const pos = new google.maps.LatLng(vehicle.lat, vehicle.lng);

            let markerImage: any;
            try {
                markerImage = await this.getVehicleIcon(vehicle, 'vehicle');
                if (!markerImage) {
                    markerImage = null;
                }
            } catch (error) {
                console.error(`Błąd przy pobieraniu ikony dla pojazdu ${vehicle.id}:`, error);
                markerImage = undefined;
            }

            let marker: any = null;
            const returnedVehicle = this.vehicleExist(vehicle, this.currentVehicleMarkers)
            const oldMarker = returnedVehicle != null ? returnedVehicle as VehicleRestModel : null;

            if (oldMarker != null) {
                marker = oldMarker.marker;
                marker.setPosition(pos);
                marker.setIcon(markerImage);
            }
            else {
                const markerOptions =
                {
                    icon: markerImage,
                    position: pos,
                    map: this.map
                };
                marker = new google.maps.Marker(markerOptions);

                vehicle.marker = marker;
                const infoWindow = new google.maps.InfoWindow();
                this.createMyInfoWindow(infoWindow, marker, vehicle, 'vehicle');
                vehicle.infoWindow = infoWindow;
                this.currentVehicleMarkers[vehicle.id] = vehicle
            }
            i++
        })

        let ia = 0
        Object.keys(this.currentRegisteredVehicleMarkers).forEach(async key => {
            const currentRegisteredVehicle = this.currentRegisteredVehicleMarkers[parseInt(key)];
            const registeredVehicleExist = this.registeredVehicleExist(currentRegisteredVehicle, registeredVehicleMarkers)

            if (registeredVehicleExist == null) {
                currentRegisteredVehicle.marker.setMap(null);
                currentRegisteredVehicle.marker = null;
                delete this.currentRegisteredVehicleMarkers[parseInt(key)]
            }
        })

        Object.keys(registeredVehicleMarkers).forEach(async key => {
            const registeredVehicle = registeredVehicleMarkers[parseInt(key)];
            const pos = new google.maps.LatLng(registeredVehicle.lat, registeredVehicle.lng);

            let markerImage: any;
            try {
                markerImage = await this.getVehicleIcon(registeredVehicle, 'registeredVehicle');
                if (!markerImage) {
                    markerImage = null;
                }
            } catch (error) {
                console.error(`Błąd przy pobieraniu ikony dla pojazdu ${registeredVehicle.id}:`, error);
                markerImage = null;
            }

            const title = `${registeredVehicle.registrationNumber} (${registeredVehicle.palletCount}) Available from: ${registeredVehicle.availableFrom}`;
            let marker: any = null;
            const returnedRegisteredVehicle = this.vehicleExist(registeredVehicle, this.currentRegisteredVehicleMarkers)
            const oldMarker = returnedRegisteredVehicle != null ? returnedRegisteredVehicle as VehicleRestModel : null;

            if (oldMarker != null) {
                marker = oldMarker.marker;
                marker.setPosition(pos);
                marker.setIcon(markerImage);
                marker.setTitle(title);
            }
            else {
                const markerOptions =
                {
                    title: title,
                    icon: markerImage,
                    position: pos,
                    map: this.map
                };
                marker = new google.maps.Marker(markerOptions);

                registeredVehicle.marker = marker;
                const infoWindow = new google.maps.InfoWindow();
                this.createMyInfoWindow(infoWindow, marker, registeredVehicle, 'registeredVehicle');
                registeredVehicle.infoWindow = infoWindow;
                this.currentRegisteredVehicleMarkers[registeredVehicle.id] = registeredVehicle
            }
            ia++
        })

        let ix = 0
        Object.keys(this.currentLogisticLoadMarkers).forEach(async key => {
            const currentLogisticLoad = this.currentLogisticLoadMarkers[parseInt(key)];
            const logisticLoadExist = this.logisticLoadExist(currentLogisticLoad, logisticLoadMarkers)

            if (logisticLoadExist == null) {
                currentLogisticLoad.marker.setMap(null);
                currentLogisticLoad.marker = null;
                delete this.currentLogisticLoadMarkers[parseInt(key)]
            }
        })

        Object.keys(logisticLoadMarkers).forEach(async key => {
            const logisticLoad = logisticLoadMarkers[parseInt(key)];
            const pos = new google.maps.LatLng(logisticLoad.pickupLat, logisticLoad.pickupLng);

            let markerImage: any;
            try {
                markerImage = await this.getLogisticLoadIcon(logisticLoad);
                if (!markerImage) {
                    markerImage = null;
                }
            } catch (error) {
                console.error(`Błąd przy pobieraniu ikony dla pojazdu ${logisticLoad.id}:`, error);
                markerImage = null;
            }

            let marker: any = null;
            const returnedLogisticLoad = this.vehicleExist(logisticLoad, this.currentLogisticLoadMarkers)
            const oldMarker = returnedLogisticLoad != null ? returnedLogisticLoad as LogisticLoadRestModel : null;

            if (oldMarker != null) {
                marker = oldMarker.marker;
                marker.setPosition(pos);
                marker.setIcon(markerImage);
            }
            else {
                const markerOptions =
                {
                    icon: markerImage,
                    position: pos,
                    map: this.map
                };
                marker = new google.maps.Marker(markerOptions);

                logisticLoad.marker = marker;
                const infoWindow = new google.maps.InfoWindow();
                this.createMyInfoWindow(infoWindow, marker, logisticLoad, 'logisticLoad');
                logisticLoad.infoWindow = infoWindow;
                this.currentLogisticLoadMarkers[logisticLoad.id] = logisticLoad
            }
            ix++
        })

        let iz = 0
        Object.keys(this.currentSupervisorUnloadingPlaceForMapMarkers).forEach(async key => {
            const currentSupervisorUnloadingPlaceForMapMarker = this.currentSupervisorUnloadingPlaceForMapMarkers[parseInt(key)];
            const supervisorUnloadingPlaceForMapExist = this.supervisorUnloadingPlaceForMapExist(currentSupervisorUnloadingPlaceForMapMarker, supervisorUnloadingPlaceForMapMarkers)

            if (supervisorUnloadingPlaceForMapExist == null) {
                currentSupervisorUnloadingPlaceForMapMarker.marker.setMap(null);
                currentSupervisorUnloadingPlaceForMapMarker.marker = null;
                delete this.currentSupervisorUnloadingPlaceForMapMarkers[parseInt(key)]
            }
        })

        Object.keys(supervisorUnloadingPlaceForMapMarkers).forEach(async key => {
            const supervisorUnloadingPlaceForMap = supervisorUnloadingPlaceForMapMarkers[parseInt(key)];
            const pos = new google.maps.LatLng(supervisorUnloadingPlaceForMap.pickupLat, supervisorUnloadingPlaceForMap.pickupLng);

            let markerImage: any;
            try {
                markerImage = await this.getVehicleIcon(supervisorUnloadingPlaceForMap, 'supervisorUnloading');
                if (!markerImage) {
                    markerImage = null;
                }
            } catch (error) {
                console.error(`Błąd przy pobieraniu ikony dla rozładunku (pojazdu) ${supervisorUnloadingPlaceForMap.id}. vehicleTypeId: ${supervisorUnloadingPlaceForMap.vehicleTypeId}. vehicleState: ${supervisorUnloadingPlaceForMap.vehicleState}. Error: ${error}. JSON: ${JSON.stringify(supervisorUnloadingPlaceForMap)}`);
                markerImage = undefined; // default Google Maps marker icon
            }

            let marker: any = null;
            const returnedSupervisorUnloadingPlaceForMap = this.vehicleExist(supervisorUnloadingPlaceForMap, this.currentSupervisorUnloadingPlaceForMapMarkers)
            const oldMarker = returnedSupervisorUnloadingPlaceForMap != null ? returnedSupervisorUnloadingPlaceForMap as SupervisorUnloadingPlaceForMapListRestModel : null;

            if (oldMarker != null) {
                marker = oldMarker.marker;
                marker.setIcon(markerImage);
                marker.setPosition(pos);
            }
            else {
                const markerOptions =
                {
                    icon: markerImage,
                    position: pos,
                    map: this.map
                };
                marker = new google.maps.Marker(markerOptions);

                supervisorUnloadingPlaceForMap.marker = marker;
                const infoWindow = new google.maps.InfoWindow();
                this.createMyInfoWindow(infoWindow, marker, supervisorUnloadingPlaceForMap, 'supervisorUnloadingPlaceForMap');
                supervisorUnloadingPlaceForMap.infoWindow = infoWindow;
                this.currentSupervisorUnloadingPlaceForMapMarkers[supervisorUnloadingPlaceForMap.id] = supervisorUnloadingPlaceForMap
            }
            iz++
        })
    }

    createMyInfoWindow(infoWindow: any, marker: any, object: any, objectType: string) {
        if (objectType == 'vehicle') {
            const that = this;
            google.maps.event.clearListeners(marker, 'click');
            google.maps.event.addListener(marker, 'click', async function () {
                infoWindow.close();
                const currentInfoWindowElement = document.getElementById("info-window") as HTMLElement;

                if (Object.keys(that.currentInfoWindow).length != 0) {
                    that.currentInfoWindow.close();
                }

                if (that.infoWindowElement == undefined) {
                    that.infoWindowElement = currentInfoWindowElement;
                }

                await that.$store.direct.dispatch.vehicle.loadVehicle({ id: object.id });
                that.showInfoWindow = true;
                infoWindow.setContent(that.infoWindowElement);
                infoWindow.open(that.map, marker);
                that.currentInfoWindow = infoWindow
            });
        }
        else if (objectType == 'registeredVehicle') {
            const that = this;
            google.maps.event.clearListeners(marker, 'click');
            google.maps.event.addListener(marker, 'click', async function () {
                infoWindow.close();
                const currentInfoWindowElement = document.getElementById("info-window") as HTMLElement;

                if (Object.keys(that.currentInfoWindow).length != 0) {
                    that.currentInfoWindow.close();
                }

                if (that.infoWindowElement == undefined) {
                    that.infoWindowElement = currentInfoWindowElement;
                }

                await that.$store.direct.dispatch.vehicle.loadRegisteredVehicle({ id: object.id });
                that.showInfoWindow = true;
                infoWindow.setContent(that.infoWindowElement);
                infoWindow.open(that.map, marker);
                that.currentInfoWindow = infoWindow
            });
        }
        else if (objectType == 'logisticLoad') {
            const that = this;
            google.maps.event.clearListeners(marker, 'click');
            google.maps.event.addListener(marker, 'click', async function () {
                infoWindow.close();
                const currentInfoWindowElement = document.getElementById("logistic-load-info-window") as HTMLElement;

                if (Object.keys(that.currentInfoWindow).length != 0) {
                    that.currentInfoWindow.close();
                }

                if (that.logisticLoadInfoWindowElement == undefined) {
                    that.logisticLoadInfoWindowElement = currentInfoWindowElement;
                }

                await that.$store.direct.dispatch.logistic.loadLogisticLoad({ id: object.id });
                that.showLogiscticLoadInfoWindow = true;
                infoWindow.setContent(that.logisticLoadInfoWindowElement);
                infoWindow.open(that.map, marker);
                that.currentInfoWindow = infoWindow
            });
        }
        else if (objectType == 'supervisorUnloadingPlaceForMap') {
            const that = this;
            google.maps.event.clearListeners(marker, 'click');
            google.maps.event.addListener(marker, 'click', async function () {
                infoWindow.close();
                const currentInfoWindowElement = document.getElementById("supervisor-unloading-place-for-map-info-window") as HTMLElement;

                if (Object.keys(that.currentInfoWindow).length != 0) {
                    that.currentInfoWindow.close();
                }

                if (that.supervisorUnloadingPlaceForMapInfoWindowElement == undefined) {
                    that.supervisorUnloadingPlaceForMapInfoWindowElement = currentInfoWindowElement;
                }

                await that.$store.direct.dispatch.supervisor.getSupervisorUnloadingPlaceForMap({ id: object.id });
                that.showSupervisorUnloadingPlaceForMapInfoWindow = true;
                infoWindow.setContent(that.supervisorUnloadingPlaceForMapInfoWindowElement);
                infoWindow.open(that.map, marker);
                that.currentInfoWindow = infoWindow
            });
        }
    }

    spreadMarkers(): void {
        if (!this.map || typeof google === "undefined" || !google.maps) return;
        const overlay = new google.maps.OverlayView();
        overlay.onAdd = function () { /* intentionally empty */ };
        overlay.draw = function () { /* intentionally empty */ };
        overlay.onRemove = function () { /* intentionally empty */ };
        overlay.setMap(this.map);

        google.maps.event.addListenerOnce(this.map, 'idle', () => {
            const projection = overlay.getProjection();
            if (!projection) return;

            const allMarkers: google.maps.Marker[] = [];

            Object.keys(this.currentVehicleMarkers).forEach(key => {
                const marker = this.currentVehicleMarkers[key].marker;
                if (marker) {
                    if (!marker.originalPosition) {
                        marker.originalPosition = marker.getPosition();
                    } else {
                        marker.setPosition(marker.originalPosition);
                    }
                    allMarkers.push(marker);
                }
            });

            Object.keys(this.currentLogisticLoadMarkers).forEach(key => {
                const marker = this.currentLogisticLoadMarkers[key].marker;
                if (marker) {
                    if (!marker.originalPosition) {
                        marker.originalPosition = marker.getPosition();
                    } else {
                        marker.setPosition(marker.originalPosition);
                    }
                    allMarkers.push(marker);
                }
            });

            Object.keys(this.currentSupervisorUnloadingPlaceForMapMarkers).forEach(key => {
                const marker = this.currentSupervisorUnloadingPlaceForMapMarkers[key].marker;
                if (marker) {
                    if (!marker.originalPosition) {
                        marker.originalPosition = marker.getPosition();
                    } else {
                        marker.setPosition(marker.originalPosition);
                    }
                    allMarkers.push(marker);
                }
            });

            interface Cluster {
                markers: google.maps.Marker[];
                center: google.maps.Point;
            }
            const clusters: Cluster[] = [];
            const tolerance = 25; // szerokość odsunięcia ikon

            allMarkers.forEach(marker => {
                const pos = marker.getPosition();
                if (!pos) return;
                const pixelPos = projection.fromLatLngToDivPixel(pos);
                let addedToCluster = false;

                for (const cluster of clusters) {
                    const dx = pixelPos.x - cluster.center.x;
                    const dy = pixelPos.y - cluster.center.y;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    if (distance < tolerance) {
                        cluster.markers.push(marker);
                        const total = cluster.markers.length;
                        cluster.center.x = (cluster.center.x * (total - 1) + pixelPos.x) / total;
                        cluster.center.y = (cluster.center.y * (total - 1) + pixelPos.y) / total;
                        addedToCluster = true;
                        break;
                    }
                }
                if (!addedToCluster) {
                    clusters.push({
                        markers: [marker],
                        center: new google.maps.Point(pixelPos.x, pixelPos.y)
                    });
                }
            });

            clusters.forEach(cluster => {
                if (cluster.markers.length > 1) {
                    const n = cluster.markers.length;
                    const offsetRadius = tolerance;
                    cluster.markers.forEach((marker, index) => {
                        const angle = (2 * Math.PI * index) / n;
                        const newX = cluster.center.x + offsetRadius * Math.cos(angle);
                        const newY = cluster.center.y + offsetRadius * Math.sin(angle);
                        const newLatLng = projection.fromDivPixelToLatLng(new google.maps.Point(newX, newY));
                        marker.setPosition(newLatLng);
                    });
                }
            });
        });
    }

    async initialMarkers() {
        if (!this.map || typeof google === "undefined" || !google.maps) {
            return;
        }

        const vehicleMarkers: any = {};
        await this.vehicleMapList.forEach((item: VehicleRestModel) => {
            vehicleMarkers[item.id] = {
                id: item.id,
                availableFrom: item.availableFrom,
                isSpecial: item.isSpecial,
                lat: item.lat,
                lng: item.lng,
                palletCount: item.palletCount,
                registrationNumber: item.registrationNumber,
                vehicleTypeId: item.vehicleTypeId,
                vehicleState: item.vehicleState,
            }
        });

        const registeredVehicleMarkers: any = {};
        await this.registeredVehicleMapList.forEach((item: VehicleRestModel) => {
            registeredVehicleMarkers[item.id] = {
                id: item.id,
                availableFrom: item.availableFrom,
                isSpecial: item.isSpecial,
                lat: item.lat,
                lng: item.lng,
                palletCount: item.palletCount,
                registrationNumber: item.registrationNumber,
                vehicleTypeId: item.vehicleTypeId,
                vehicleState: item.vehicleState,
            }
        });

        const logisticLoadMarkers: any = {};
        await this.logisticLoadMapList.forEach((item: LogisticLoadRestModel) => {
            logisticLoadMarkers[item.id] = {
                id: item.id,
                pickupLat: item.pickupLat,
                pickupLng: item.pickupLng,
                loadTypeId: item.loadTypeId,
                loadStateId: item.loadStateId,
            }
        });

        const supervisorUnloadingPlaceForMapMarkers: any = {};
        await this.supervisorUnloadingPlaceForMapList.forEach((item: SupervisorUnloadingPlaceForMapListRestModel) => {
            supervisorUnloadingPlaceForMapMarkers[item.id] = {
                id: item.id,
                pickupLat: item.lat,
                pickupLng: item.lng,
                vehicleTypeId: item.vehicleTypeId,
                vehicleState: item.vehicleState,
            }
        });
        await this.setupMarkers(vehicleMarkers, registeredVehicleMarkers, logisticLoadMarkers, supervisorUnloadingPlaceForMapMarkers);
        this.spreadMarkers();
    }

    async loadMap() {
        await this.loader.load().then(async () => {
            this.map = new google.maps.Map(document.getElementById("map") as HTMLElement, {
                center: {
                    lat: 50,
                    lng: 15
                },
                zoom: 5,
                mapTypeControl: true
            });
            await this.initialMarkers()
            this.map.addListener('idle', () => {
                this.spreadMarkers();
            });

            this.directionsService = new google.maps.DirectionsService();
            this.directionsRenderer = new google.maps.DirectionsRenderer();
            this.directionsRenderer.setMap(this.map);
        })
    }

    get vehicleIcons(): any {
        return this.$store.direct.state.common.vehicleIcons;
    }

    async getVehicleIcon(vehicle: VehicleRestModel | SupervisorUnloadingPlaceForMapListRestModel, vehicleIconType: string) {
        const vehicleTypeId = vehicle.vehicleTypeId;
        const vehicleState = vehicle.vehicleState;

        let vehicleIconTypeKey = 0;
        if (vehicleIconType == 'supervisorUnloading') {
            vehicleIconTypeKey = 1;
        }
        else if (vehicleIconType == 'registeredVehicle') {
            vehicleIconTypeKey = 2;
        }
        const iconKey = vehicleTypeId + '_' + vehicleState + '_' + vehicleIconTypeKey;
        const icon = this.vehicleIcons[iconKey]

        if (icon == undefined) {
            await this.$store.direct.dispatch.common.loadVehicleIcon({ typeId: vehicleTypeId, state: vehicleState, key: iconKey, vehicleIconType: vehicleIconType });
        }

        return this.vehicleIcons[iconKey]
    }

    get logisticLoadIcons(): any {
        return this.$store.direct.state.logistic.logisticLoadIcons;
    }

    async getLogisticLoadIcon(logisticLoad: LogisticLoadRestModel) {
        const logisticLoadTypeId = logisticLoad.loadTypeId;
        const logisticLoadStateId = logisticLoad.loadStateId;
        const iconKey = logisticLoadTypeId + '_' + logisticLoadStateId;
        const icon = this.logisticLoadIcons[iconKey];

        if (icon == undefined) {
            await this.$store.direct.dispatch.logistic.loadLogisticLoadIcon({ typeId: logisticLoadTypeId, stateId: logisticLoadStateId });
        }

        return this.logisticLoadIcons[iconKey];
    }

    async loadVehiclesData() {
        if (this.localStorageApplicationStateData.mapPageVehicleLayer == true) {
            await this.$store.direct.dispatch.vehicle.loadVehicleMapList({
                adr: this.localStorageApplicationStateData.filterByAdr,
                tailLift: this.localStorageApplicationStateData.filterByTailLift,
                contractorSearchText: this.localStorageApplicationStateData.contractorSearchItem?.text
            });
        }
        else {
            this.vehicleMapList = [] as VehicleRestModel[];
        }
    }

    async loadRegisteredVehiclesData() {
        if (this.localStorageApplicationStateData.mapPageRegisteredVehicleLayer == true) {
            await this.$store.direct.dispatch.vehicle.loadRegisteredVehicleMapList({
                contractorSearchText: this.localStorageApplicationStateData.contractorSearchItem?.text
            });
        }
        else {
            this.registeredVehicleMapList = [] as VehicleRestModel[];
        }
    }

    async loadLogisticLoadsData() {
        if (this.localStorageApplicationStateData.mapPageLogisticLoadLayer == true) {
            await this.$store.direct.dispatch.logistic.loadLogisticLoadMapList();
        }
        else {
            this.logisticLoadMapList = [] as LogisticLoadRestModel[];
        }
    }

    async loadSupervisorUnloadingPlaceForMapData() {
        if (this.localStorageApplicationStateData.mapPageSupervisorUnloadingPlaceLayer == true) {
            await this.$store.direct.dispatch.supervisor.getSupervisorUnloadingPlaceForMapList(
                {
                    showBmwUnloadings: this.localStorageApplicationStateData.mapPageSupervisorBmwUnloadingPlaceLayer,
                    showEncoryUnloadings: this.localStorageApplicationStateData.mapPageSupervisorEncoryUnloadingPlaceLayer,
                    adr: this.localStorageApplicationStateData.filterByAdr,
                    tailLift: this.localStorageApplicationStateData.filterByTailLift,
                    contractorSearchText: this.localStorageApplicationStateData.contractorSearchItem?.text
                });
        }
        else {
            this.supervisorUnloadingPlaceForMapList = [] as SupervisorUnloadingPlaceForMapListRestModel[];
        }
    }

    async reloadVehicles() {
        await this.loadVehiclesData();
        await this.initialMarkers();
    }

    async reloadRegisteredVehicles() {
        await this.loadRegisteredVehiclesData();
        await this.initialMarkers();
    }

    async reloadLogisticLoads() {
        await this.loadLogisticLoadsData();
        await this.initialMarkers();
    }

    async reloadSupervisorUnloadingPlaceForMapList() {
        await this.loadSupervisorUnloadingPlaceForMapData();
        await this.initialMarkers();
    }

    async reloadByAdrTailLiftFilter() {
        await Promise.all([
            this.loadVehiclesData(),
            this.loadSupervisorUnloadingPlaceForMapData(),
        ]);
        await this.initialMarkers();
    }

    async reloadByContractorFilter() {
        await Promise.all([
            this.loadVehiclesData(),
            this.loadRegisteredVehiclesData(),
            this.loadSupervisorUnloadingPlaceForMapData(),
        ]);
        await this.initialMarkers();
    }

    async clearContractorFilter() {
        this.localStorageApplicationStateData.contractorSearchItem = null;
        this.contractorMapSearch = "";
        await this.reloadByContractorFilter();
    }

    async refreshAllActiveFilters() {
        const promises = [];

        if ((this.contractorMapSearch ?? "").length > 0) {
            this.$store.direct.dispatch.common.searchContractors({
                searchText: this.contractorMapSearch,
                contractorTypes: [],
                contractorPropertiesEnabled: [],
                source: "map",
                searchOnlyActive: true
            });
        }

        if (this.localStorageApplicationStateData.mapPageVehicleLayer) {
            promises.push(this.loadVehiclesData());
        }
        if (this.localStorageApplicationStateData.mapPageRegisteredVehicleLayer) {
            promises.push(this.loadRegisteredVehiclesData());
        }
        if (this.localStorageApplicationStateData.mapPageLogisticLoadLayer) {
            promises.push(this.loadLogisticLoadsData());
        }
        if (this.localStorageApplicationStateData.mapPageSupervisorUnloadingPlaceLayer) {
            promises.push(this.loadSupervisorUnloadingPlaceForMapData());
        }

        await Promise.all(promises);
        await this.initialMarkers();
    }

}
